/**
 * ML Authentication Client - SIH API Integration
 * Calls Python Flask server for certificate authenticity verification
 * 
 * API Endpoints:
 * - POST /predict - Certificate authentication
 * - GET /health - Server health check
 * - GET /model-info - Model information
 */

class MLAuthenticationClient {
  constructor() {
    // Configure API endpoint based on environment
    this.apiUrl = 'http://127.0.0.1:5000';
    
    this.isAvailable = null; // Cache availability status
    this.modelInfo = null;   // Cache model information
  }

  /**
   * Check if ML API server is available
   */
  async checkAvailability() {
    if (this.isAvailable !== null) {
      return this.isAvailable;
    }

    try {
      console.log('� Checking ML API availability at:', this.apiUrl);
      
      const response = await fetch(`${this.apiUrl}/health`, {
        method: 'GET',
        timeout: 5000, // 5 second timeout
      });
      
      if (response.ok) {
        const healthData = await response.json();
        this.isAvailable = healthData.model_loaded;
        console.log('✅ ML API is available:', healthData);
        return this.isAvailable;
      } else {
        this.isAvailable = false;
        console.warn('⚠️ ML API health check failed:', response.status);
        return false;
      }
      
    } catch (error) {
      console.warn('⚠️ ML API not available:', error.message);
      this.isAvailable = false;
      return false;
    }
  }

  /**
   * Get ML model information from server
   */
  async getModelInfo() {
    if (this.modelInfo) {
      return this.modelInfo;
    }

    try {
      const response = await fetch(`${this.apiUrl}/model-info`);
      
      if (response.ok) {
        this.modelInfo = await response.json();
        console.log('📊 ML Model Info:', this.modelInfo);
        return this.modelInfo;
      } else {
        console.warn('⚠️ Failed to get model info:', response.status);
        return null;
      }
      
    } catch (error) {
      console.error('❌ Model info request failed:', error);
      return null;
    }
  }

  /**
   * Predict certificate authenticity using the SIH ML API
   * 
   * @param {File} file - The certificate image file
   * @returns {Promise<Object>} SIH-compatible prediction result
   */
  async predictAuthenticity(file) {
    try {
      console.log('🔍 Starting SIH ML API authentication for:', file.name);

      // Check if API is available
      const apiAvailable = await this.checkAvailability();
      
      if (!apiAvailable) {
        console.log('⚠️ ML API unavailable, generating mock prediction');
        return this.generateMockPrediction(file);
      }

      // Prepare form data for API
      const formData = new FormData();
      formData.append('image', file);

      console.log('🚀 Sending request to SIH ML API...');
      
      const response = await fetch(`${this.apiUrl}/predict`, {
        method: 'POST',
        body: formData,
        // Don't set Content-Type - let browser set it with boundary for FormData
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(`API request failed: ${response.status} - ${errorData.error || 'Unknown error'}`);
      }

      const result = await response.json();
      
      console.log('✅ SIH ML API Response:', result);

      // Transform API response to match expected format
      const transformedResult = {
        isAuthentic: result.authentic,
        prediction: result.prediction,
        confidence: Math.round(result.confidence * 100),
        confidenceStatus: result.confidence_level,
        realProbability: Math.round(result.real_probability * 100),
        fakeProbability: Math.round(result.fake_probability * 100),
        details: {
          modelType: result.model_type,
          architecture: 'SIH MobileNetV2 + Dense layers',
          inputSize: '224x224x3',
          outputClasses: ['REAL_CERT', 'FAKE_CERT'],
          classMapping: { 0: 'REAL_CERT', 1: 'FAKE_CERT' },
          predictedIndex: result.predicted_index,
          isMockModel: result.mock_result || false,
          apiEndpoint: this.apiUrl,
          timestamp: result.timestamp,
          imageSize: result.image_size
        }
      };

      console.log('🎯 Final ML Result:', {
        prediction: transformedResult.prediction,
        confidence: `${transformedResult.confidence}%`,
        level: transformedResult.confidenceStatus
      });

      return transformedResult;
      
    } catch (error) {
      console.error('❌ SIH ML API authentication failed:', error);
      
      // Fallback to mock prediction on any API error
      console.log('🔄 Falling back to mock prediction');
      return this.generateMockPrediction(file, error.message);
    }
  }

  /**
   * Generate mock prediction when API unavailable
   */
  generateMockPrediction(file, errorMessage = null) {
    // Generate consistent mock predictions based on file characteristics
    const hash = this.simpleHash(file.name + file.size);
    const isAuthentic = hash % 3 !== 0; // ~67% authentic rate
    const confidence = 65 + (hash % 30); // 65-95% range
    
    const result = {
      isAuthentic,
      prediction: isAuthentic ? 'REAL_CERT' : 'FAKE_CERT',
      confidence: confidence,
      confidenceStatus: confidence > 85 ? 'HIGH' : confidence > 70 ? 'MODERATE' : 'LOW',
      realProbability: isAuthentic ? confidence : 100 - confidence,
      fakeProbability: isAuthentic ? 100 - confidence : confidence,
      details: {
        modelType: 'Mock_SIH_API',
        architecture: 'Simulated MobileNetV2',
        inputSize: '224x224x3',
        outputClasses: ['REAL_CERT', 'FAKE_CERT'],
        classMapping: { 0: 'REAL_CERT', 1: 'FAKE_CERT' },
        predictedIndex: isAuthentic ? 0 : 1,
        isMockModel: true,
        apiEndpoint: this.apiUrl,
        timestamp: new Date().toISOString(),
        error: errorMessage
      }
    };
    
    console.log('🎭 Mock prediction generated:', result);
    return result;
  }

  /**
   * Simple hash function for consistent mock predictions
   */
  simpleHash(str) {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash;
    }
    return Math.abs(hash);
  }

  /**
   * Test the ML API connection and model
   */
  async testModel() {
    try {
      console.log('🧪 Testing SIH ML API connection...');
      
      const apiAvailable = await this.checkAvailability();
      const modelInfo = await this.getModelInfo();
      
      return {
        status: apiAvailable ? 'success' : 'unavailable',
        message: apiAvailable 
          ? '✅ SIH ML API is online and model loaded'
          : '⚠️ SIH ML API unavailable - using mock predictions',
        apiUrl: this.apiUrl,
        modelInfo: modelInfo,
        timestamp: new Date().toISOString()
      };
      
    } catch (error) {
      return {
        status: 'error',
        message: `❌ SIH ML API test failed: ${error.message}`,
        apiUrl: this.apiUrl,
        error: error.message,
        timestamp: new Date().toISOString()
      };
    }
  }
}

// Export singleton instance
export const mlAuthClient = new MLAuthenticationClient();

/**
 * Convenience function for SIH certificate authentication
 * @param {File} file - Certificate image file  
 * @returns {Promise<Object>} SIH authentication result with detailed analysis
 */
export const authenticateWithML = async (file) => {
  return await mlAuthClient.predictAuthenticity(file);
};

export default MLAuthenticationClient;